// Attribution Component - Handles map attribution only
// Map attribution: bottom-right corner of map
// Menu attribution: map data text in left menu (created in header-menu-left.js)
import { getText } from '../translations/translations.js';
import { hasSeenGuide } from '../utils/guide.js';

// Attribution URLs
const ATTRIBUTION_URL = 'https://www.openstreetmap.org/copyright';

// Initialize map attribution (bottom-right corner)
export function initializeAttribution() {
    // Check if guide has been seen - if not, wait for guide to close
    if (!hasSeenGuide()) {
        // Wait for guide to close before showing attribution
        document.addEventListener('guide-closed', () => {
            import('/settings.js').then(settings => {
                if (settings.ATTRIBUTION_SETTINGS && settings.ATTRIBUTION_SETTINGS.SHOW_IN_MAP) {
                    createMapAttribution();
                }
            }).catch(error => {
                console.warn('Could not load attribution settings, showing attribution by default:', error);
                createMapAttribution();
            });
        }, { once: true });
        return;
    }

    // Guide has been seen, show attribution normally
    import('/settings.js').then(settings => {
        // Check if attribution should be shown in map
        if (settings.ATTRIBUTION_SETTINGS && settings.ATTRIBUTION_SETTINGS.SHOW_IN_MAP) {
            createMapAttribution();
        }
    }).catch(error => {
        console.warn('Could not load attribution settings, showing attribution by default:', error);
        // Fallback: show attribution if settings can't be loaded
        createMapAttribution();
    });
}

// Create map attribution element
function createMapAttribution() {
    const attributionContainer = document.createElement('div');
    attributionContainer.className = 'map-attribution';
    attributionContainer.innerHTML = `
        <a href="${ATTRIBUTION_URL}" target="_blank" rel="noopener noreferrer">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="attribution-icon">
                <path d="M576 112C576 100.9 570.3 90.6 560.8 84.8C551.3 79 539.6 78.4 529.7 83.4L413.5 141.5L234.1 81.6C226 78.9 217.3 79.5 209.7 83.3L81.7 147.3C70.8 152.8 64 163.9 64 176L64 528C64 539.1 69.7 549.4 79.2 555.2C88.7 561 100.4 561.6 110.3 556.6L226.4 498.5L399.7 556.3C395.4 549.9 391.2 543.2 387.1 536.4C376.1 518.1 365.2 497.1 357.1 474.6L255.9 440.9L255.9 156.4L383.9 199.1L383.9 298.4C414.9 262.6 460.9 240 511.9 240C534.5 240 556.1 244.4 575.9 252.5L576 112zM512 288C445.7 288 392 340.8 392 405.9C392 474.8 456.1 556.3 490.6 595.2C502.2 608.2 521.9 608.2 533.5 595.2C568 556.3 632.1 474.8 632.1 405.9C632.1 340.8 578.4 288 512.1 288zM472 408C472 385.9 489.9 368 512 368C534.1 368 552 385.9 552 408C552 430.1 534.1 448 512 448C489.9 448 472 430.1 472 408z"/>
            </svg>
            ${getText('attribution.map')}
        </a>
    `;

    // Add to map container
    const mapContainer = document.getElementById('map');
    if (mapContainer) {
        mapContainer.appendChild(attributionContainer);

        // Auto-hide attribution after specified seconds (if configured)
        import('/settings.js').then(settings => {
            const hideDelay = settings.ATTRIBUTION_SETTINGS?.AUTO_HIDE_SECONDS;
            if (hideDelay && hideDelay > 0) {
                setTimeout(() => {
                    if (attributionContainer && attributionContainer.parentNode) {
                        attributionContainer.style.transition = 'opacity 0.5s ease-out';
                        attributionContainer.style.opacity = '0';
                        setTimeout(() => {
                            if (attributionContainer && attributionContainer.parentNode) {
                                attributionContainer.remove();
                            }
                        }, 500); // Wait for fade-out animation
                    }
                }, hideDelay * 1000); // Convert seconds to milliseconds
            }
        }).catch(error => {
            // Silently fail if settings can't be loaded
            console.warn('Could not load attribution auto-hide settings:', error);
        });
    }
}

// Create menu attribution text (map data only - no logo)
// Logo and info links are handled in header-menu-left.js
export function createMenuAttributionText() {
    return new Promise((resolve) => {
        // Import settings dynamically to avoid circular imports
        import('/settings.js').then(settings => {
            // Check if attribution should be shown in menu
            if (settings.ATTRIBUTION_SETTINGS && settings.ATTRIBUTION_SETTINGS.SHOW_IN_MENU) {
                const OSM_URL = 'https://www.openstreetmap.org/copyright';
                const GEOFABRIK_URL = 'https://www.geofabrik.de/';

                const mapPart = getText('attribution.mapPart');
                const connector = getText('attribution.connector');
                const providerPart = getText('attribution.providerPart');

                const attribution = document.createElement('div');
                attribution.className = 'menu-attribution';

                // Line 1: OSM
                const firstLine = document.createElement('div');
                firstLine.className = 'attribution-line';
                firstLine.innerHTML = `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="attribution-icon">
                            <path d="M576 112C576 100.9 570.3 90.6 560.8 84.8C551.3 79 539.6 78.4 529.7 83.4L413.5 141.5L234.1 81.6C226 78.9 217.3 79.5 209.7 83.3L81.7 147.3C70.8 152.8 64 163.9 64 176L64 528C64 539.1 69.7 549.4 79.2 555.2C88.7 561 100.4 561.6 110.3 556.6L226.4 498.5L399.7 556.3C395.4 549.9 391.2 543.2 387.1 536.4C376.1 518.1 365.2 497.1 357.1 474.6L255.9 440.9L255.9 156.4L383.9 199.1L383.9 298.4C414.9 262.6 460.9 240 511.9 240C534.5 240 556.1 244.4 575.9 252.5L576 112zM512 288C445.7 288 392 340.8 392 405.9C392 474.8 456.1 556.3 490.6 595.2C502.2 608.2 521.9 608.2 533.5 595.2C568 556.3 632.1 474.8 632.1 405.9C632.1 340.8 578.4 288 512.1 288zM472 408C472 385.9 489.9 368 512 368C534.1 368 552 385.9 552 408C552 430.1 534.1 448 512 448C489.9 448 472 430.1 472 408z"/>
                        </svg>`;

                const match = mapPart.match(/OpenStreetMap contributors/i) || mapPart.match(/Colaboradores de OpenStreetMap/i) || mapPart.match(/OpenStreetMap/i);
                if (match) {
                    const before = mapPart.slice(0, match.index);
                    const anchorLabel = match[0];
                    const after = mapPart.slice(match.index + anchorLabel.length);

                    firstLine.appendChild(document.createTextNode(before));
                    const osmLink = document.createElement('a');
                    osmLink.href = OSM_URL;
                    osmLink.target = '_blank';
                    osmLink.rel = 'noopener noreferrer';
                    osmLink.textContent = anchorLabel;
                    firstLine.appendChild(osmLink);
                    firstLine.appendChild(document.createTextNode(after));
                } else {
                    firstLine.appendChild(document.createTextNode(mapPart));
                }

                // Line 2: GeoFabrik
                const secondLine = document.createElement('div');
                secondLine.className = 'attribution-line';
                secondLine.innerHTML = `
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 640" class="attribution-icon">
                            <path d="M352 96C352 78.3 337.7 64 320 64C302.3 64 288 78.3 288 96L288 306.7L246.6 265.3C234.1 252.8 213.8 252.8 201.3 265.3C188.8 277.8 188.8 298.1 201.3 310.6L297.3 406.6C309.8 419.1 330.1 419.1 342.6 406.6L438.6 310.6C451.1 298.1 451.1 277.8 438.6 265.3C426.1 252.8 405.8 252.8 393.3 265.3L352 306.7L352 96zM160 384C124.7 384 96 412.7 96 448L96 480C96 515.3 124.7 544 160 544L480 544C515.3 544 544 515.3 544 480L544 448C544 412.7 515.3 384 480 384L433.1 384L376.5 440.6C345.3 471.8 294.6 471.8 263.4 440.6L206.9 384L160 384zM464 440C477.3 440 488 450.7 488 464C488 477.3 477.3 488 464 488C450.7 488 440 477.3 440 464C440 450.7 450.7 440 464 440z"/>
                        </svg>`;
                secondLine.appendChild(document.createTextNode(`${connector} `));
                const providerLink = document.createElement('a');
                providerLink.href = GEOFABRIK_URL;
                providerLink.target = '_blank';
                providerLink.rel = 'noopener noreferrer';
                providerLink.textContent = providerPart;
                secondLine.appendChild(providerLink);

                attribution.appendChild(firstLine);
                attribution.appendChild(secondLine);
                resolve(attribution);
            } else {
                resolve(null);
            }
        }).catch(error => {
            console.warn('Could not load attribution settings:', error);
            resolve(null);
        });
    });
}
